import React, { useEffect, useState } from 'react';
import KPICards from '../components/KPICards';
import TimelineChart from '../components/TimelineChart';
import UsageBarChart from '../components/UsageBarChart';
import LangPieChart from '../components/LangPieChart';
import axios from 'axios';



// Backend API base URL
// Set REACT_APP_API_BASE to your backend origin (e.g., https://my-backend.azurewebsites.net)
// Defaults to local dev
const API_BASE = (process.env.REACT_APP_API_BASE || 'http://localhost:5000') + '/analytics';

const INTERNAL_COMPANIES = [
  'TEDATA GmbH',
  'TEDATa',
  'Driveconcepts GmbH',
  'MDESIGN',
  'MDESIGN Vertriebs GmbH',
];

const INTERNAL_COMPANIES_NORMALIZED = INTERNAL_COMPANIES.map((name) => name.toLowerCase());

function Dashboard() {
  // State for analytics data
  const [timeline, setTimeline] = useState([]);
  const [usage, setUsage] = useState([]);
  const [lang, setLang] = useState([]);
  const [sessions, setSessions] = useState({});
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [period, setPeriod] = useState('day');
  const [externOnly, setExternOnly] = useState(false);
  

  // Fetch all analytics data on mount
  useEffect(() => {
    async function fetchData() {
      const res = await axios.get(`${API_BASE}/timeline`);
      console.log("timeline typeof", typeof res.data, res.data);
      setTimeline(res.data);

      setLoading(true);
      try {
        const [timelineRes, usageRes, langRes, sessionsRes] = await Promise.all([
          axios.get(`${API_BASE}/timeline`),
          axios.get(`${API_BASE}/usage`),
          axios.get(`${API_BASE}/lang`),
          axios.get(`${API_BASE}/sessions`),
        ]);
        setTimeline(timelineRes.data);
        // usage/lang will be computed from sessions when date filters are applied
        setUsage(usageRes.data);
        setLang(langRes.data);
        setSessions(sessionsRes.data);
      } catch (err) {
        // eslint-disable-next-line
        console.error('Error fetching analytics:', err);
      }
      setLoading(false);
    }
    fetchData();
  }, []);

  // initialize default period to Day on first load
  useEffect(() => {
    applyPeriod('day');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate KPIs
  // Helper to apply quick period selection (day/week/month/year)
  const applyPeriod = (value) => {
    setPeriod(value);
    if (value === 'custom') return;
    const today = new Date();
    const end = new Date(today);
    const start = new Date(today);
    if (value === 'day') start.setDate(start.getDate() - 1);
    if (value === 'week') start.setDate(start.getDate() - 7);
    if (value === 'month') start.setMonth(start.getMonth() - 1);
    if (value === 'year') start.setFullYear(start.getFullYear() - 1);
    const toDateStr = (d) => d.toISOString().slice(0, 10);
    setEndDate(toDateStr(end));
    setStartDate(toDateStr(start));
  };

  // Helper to check if a timestamp string falls within the selected date range (inclusive)
  const inRange = (ts) => {
    if (!ts) return false;
    const d = new Date(ts);
    if (startDate) {
      const s = new Date(startDate + 'T00:00:00');
      if (d < s) return false;
    }
    if (endDate) {
      const e = new Date(endDate + 'T23:59:59.999');
      if (d > e) return false;
    }
    return true;
  };

  const isExternalMessage = (msg) => {
    const firma = (msg?.firma || '').trim().toLowerCase();
    return !INTERNAL_COMPANIES_NORMALIZED.includes(firma);
  };

  // Derive filtered analytics from sessions when date filters are present.
  // We'll compute: filteredSessions (only sessions with >=1 message in range), totalMessages, usageByFirma, langCounts, filteredTimeline
  const allSessionIds = Object.keys(sessions);
  let totalSessions = allSessionIds.length;
  let totalMessages = Object.values(sessions).reduce((sum, arr) => sum + arr.length, 0);
  let filteredTimeline = timeline;
  let usageByFirma = {};
  let langCounts = {};
  let distinctFirmen = new Set();
  let distinctUsers = new Set();

  if (startDate || endDate || externOnly) {
    // reset counters
    totalSessions = 0;
    totalMessages = 0;
    usageByFirma = {};
    langCounts = {};
    distinctFirmen = new Set();
    distinctUsers = new Set();
    const timelineByDay = {};

    for (const id of allSessionIds) {
      const msgs = sessions[id] || [];
      const filteredMsgs = msgs.filter((m) => inRange(m.timestamp) && (!externOnly || isExternalMessage(m)));
      if (filteredMsgs.length === 0) continue;

      totalSessions += 1;
      totalMessages += filteredMsgs.length;
      for (const m of filteredMsgs) {
        const f = m.firma || 'Unknown';
        usageByFirma[f] = (usageByFirma[f] || 0) + 1;
        distinctFirmen.add(f);
        const u = m.user || 'Unknown';
        distinctUsers.add(u);
        const L = m.lang || 'unknown';
        langCounts[L] = (langCounts[L] || 0) + 1;
        if (m.timestamp) {
          const day = m.timestamp.slice(0, 10);
          if (day) timelineByDay[day] = (timelineByDay[day] || 0) + 1;
        }
      }
    }

    filteredTimeline = Object.entries(timelineByDay)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([day, count]) => ({ day, count }));
  } else {
    // no filter: use provided timeline, usage, lang and totals
    filteredTimeline = timeline;
    // usage and lang come from API responses; keep shapes
    usageByFirma = usage.reduce((acc, it) => { acc[it.firma] = it.count; return acc; }, {});
    langCounts = lang.reduce((acc, it) => { acc[it.lang] = it.count; return acc; }, {});
    // compute distinct firmen and users from all sessions
    for (const id of allSessionIds) {
      const msgs = sessions[id] || [];
      for (const m of msgs) {
        const f = m.firma || 'Unknown';
        distinctFirmen.add(f);
        const u = m.user || 'Unknown';
        distinctUsers.add(u);
      }
    }
  }

  const avgMessages = totalSessions ? (totalMessages / totalSessions).toFixed(1) : 0;
  const totalFirmen = distinctFirmen.size;
  const totalUsers = distinctUsers.size;
  // convert usageByFirma/langCounts to arrays suitable for charts
  const usageData = Object.entries(usageByFirma).map(([firma, count]) => ({ firma, count }));
  const langData = Object.entries(langCounts).map(([langKey, count]) => ({ lang: langKey, count }));

  return (
    
    <div className="min-h-screen bg-gray-100 p-4 md:p-8">
       
      <h1 className="text-2xl font-bold mb-6 text-center">Chatbot-Analyse-Dashboard</h1>
      {loading ? (
        <div className="text-center text-gray-500">Wird geladen...</div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-4">
            <KPICards totalSessions={totalSessions} totalMessages={totalMessages} avgMessages={avgMessages} totalFirmen={totalFirmen} totalUsers={totalUsers} />
            {/* Date range controls for analytics */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <label className="text-xs text-gray-500" htmlFor="dashboard-extern-only-toggle">Nur Extern</label>
                <button
                  id="dashboard-extern-only-toggle"
                  type="button"
                  role="switch"
                  aria-checked={externOnly}
                  onClick={() => setExternOnly((v) => !v)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${externOnly ? 'bg-blue-600' : 'bg-gray-300'}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${externOnly ? 'translate-x-6' : 'translate-x-1'}`}
                  />
                </button>
              </div>
              <div className="flex items-end gap-2">
              <div className="flex flex-col">
                <label className="text-xs text-gray-500">Zeitraum</label>
                <select
                  className="mt-1 p-2 border rounded"
                  value={period}
                  onChange={(e) => applyPeriod(e.target.value)}
                >
                  <option value="custom">Benutzerdefiniert</option>
                  <option value="day">Tag</option>
                  <option value="week">Woche</option>
                  <option value="month">Monat</option>
                  <option value="year">Jahr</option>
                </select>
              </div>
              <div className="flex flex-col">
                <label className="text-xs text-gray-500">Von</label>
                <input
                  type="date"
                  className="mt-1 p-2 border rounded"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setPeriod('custom'); }}
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs text-gray-500">Bis</label>
                <input
                  type="date"
                  className="mt-1 p-2 border rounded"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setPeriod('custom'); }}
                />
              </div>
              <button className="ml-2 px-3 py-2 bg-gray-100 rounded text-sm" onClick={() => { setStartDate(''); setEndDate(''); setPeriod('custom'); }}>
                Löschen
              </button>
              </div>
            </div>
          </div>
          <TimelineChart data={filteredTimeline} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <UsageBarChart data={usageData} />
            <LangPieChart data={langData} />
          </div>
        </>
      )}
    </div>
  );
}


export default Dashboard;
